import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './OrderDetailsPage.module.css';

import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';

import { OrderService } from 'Logic/OrderService';
import { formatDateTime, formatMoney } from 'Logic/Functions/UtilsFunctions';

import useImageUploadRules from 'Logic/Functions/Hooks/useImageUploadRules';
import type { OrderDetails } from '../../../Model/OrdersHistory.model';

import CheckoutSummaryCard from '../Checkout/CheckoutSummaryCard';

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { authenticationState } = UserAuthenticationStoreImplementation();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const token = authenticationState?.token?.trim() ?? '';
  const isLoggedIn = !!token;
  const service = new OrderService();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const showLoading = loading;
  const showError = !!apiError;
  const showEmpty = !loading && !apiError && !order;
  const showContent = !loading && !apiError && !!order;
  const { buildImageSrc } = useImageUploadRules({
    mode: 'image',
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  useEffect(() => {
    service.handleGetOrderDetails({
      token,
      orderId: String(id ?? ''),
      setLoading,
      setApiError,
      onAuthError,
      onForbidden: (msg: string) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
      setOrder,
    });
  }, [id, isLoggedIn, token]);

  const itemsSubtotal = useMemo(() => service.computeOrderItemsSubtotal(order), [order]);

  return (
    <main className={styles.page} data-testid="order-details-page">
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/my-orders')}
            data-testid="order-details-back"
          >
            ← Back
          </button>

          <div className={styles.heading}>
            <h2 className={styles.title} data-testid="order-details-title">
              Order details
            </h2>

            {order?.orderNumber && (
              <div className={styles.orderNumber} data-testid="order-details-orderNumber">
                {order.orderNumber}
              </div>
            )}
          </div>
        </div>

        {showLoading && (
          <div className={styles.loading} data-testid="order-details-loading">
            Loading order...
          </div>
        )}

        {showError && (
          <div className={styles.error} data-testid="order-details-error">
            {apiError}
          </div>
        )}

        {showEmpty && (
          <div className={styles.empty} data-testid="order-details-empty">
            Order not found.
          </div>
        )}

        {showContent && order && (
          <div className={styles.grid} data-testid="order-details-content">
            <section className={styles.card} data-testid="order-details-summary">
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Summary</div>

                <span
                  className={`${styles.statusPill} ${styles[`status${order.status}`]}`}
                  data-testid="order-details-status"
                >
                  {order.status}
                </span>
              </div>

              <div className={styles.infoList}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Created</span>
                  <span className={styles.infoValue} data-testid="order-details-createdAt">
                    {formatDateTime(order.createdAt)}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Last update</span>
                  <span className={styles.infoValue} data-testid="order-details-lastModifiedAt">
                    {formatDateTime(order.lastModifiedAt)}
                  </span>
                </div>
              </div>

              <div className={styles.divider} />

              <div className={styles.infoList}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Items subtotal</span>
                  <span className={styles.infoValue} data-testid="order-details-subtotal">
                    {formatMoney(itemsSubtotal)}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Delivery</span>
                  <span className={styles.infoValue} data-testid="order-details-deliveryCost">
                    {formatMoney(order.deliveryCost)}
                  </span>
                </div>

                <div className={styles.infoRowStrong}>
                  <span className={styles.infoLabel}>Total</span>
                  <span className={styles.infoValue} data-testid="order-details-total">
                    {formatMoney(order.totalToBePaid)}
                  </span>
                </div>
              </div>
            </section>

            <section className={styles.card} data-testid="order-details-shipping">
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Delivery & Payment</div>
              </div>

              <div className={styles.infoList}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Payment</span>
                  <span className={styles.infoValue} data-testid="order-details-payment">
                    {order.paymentMethod.name}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Carrier</span>
                  <span className={styles.infoValue} data-testid="order-details-carrier">
                    {order.deliveryCarrier.name}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>ETA</span>
                  <span className={styles.infoValue} data-testid="order-details-eta">
                    {order.deliveryCarrier.deliveryTimeEstimation}
                  </span>
                </div>

                <div className={styles.divider} />

                <div className={styles.blockTitle}>Address</div>

                <div className={styles.address} data-testid="order-details-address">
                  <div className={styles.addrLine}>
                    <strong>{order.deliveryAddress.addressLabel}</strong>
                  </div>
                  <div className={styles.addrLine}>
                    {order.deliveryAddress.street} {order.deliveryAddress.houseNumber}
                  </div>
                  <div className={styles.addrLine}>
                    {order.deliveryAddress.city}, {order.deliveryAddress.region}
                  </div>
                  <div className={styles.addrLine}>
                    {order.deliveryAddress.country}
                    {order.deliveryAddress.postalCode
                      ? ` - ${order.deliveryAddress.postalCode}`
                      : ''}
                  </div>

                  {order.deliveryAddress.additionalInformation && (
                    <div className={styles.addrNote}>
                      {order.deliveryAddress.additionalInformation}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className={styles.cardWide} data-testid="order-details-items">
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Items</div>
                <div className={styles.itemsCount} data-testid="order-details-itemsCount">
                  {order.items.length} item(s)
                </div>
              </div>

              <ul className={styles.itemsUl} data-testid="order-details-items-list">
                {order.items.map((it) => (
                  <CheckoutSummaryCard
                    key={it.id}
                    item={{
                      id: it.id,
                      name: it.name,
                      quantity: Number(it.quantity || 0),
                      unitPrice: Number(it.unitPrice || 0),
                      totalPrice: Number(it.totalPrice || 0),
                    }}
                    imageSrc={buildImageSrc(it.imageUrl ?? null)}
                  />
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </main>
  );
};

export default OrderDetailsPage;
